import { isImage, isVideo, isAudio, isDocument } from 'utilities/file';

import { xlsxTypes } from 'application/dom-utility';

class StartupHelpdesk {

  constructor(basePath, apiClient) {

    this.basePath = `${basePath}/helpdesk`;
    this.apiClient = apiClient;

  }

  async bootstrap(query, urlParameters) {

    const path = `${this.basePath}/inbox/bootstrap`;

    return this.apiClient.post(path, {}, {}, { query, urlParameters });

  }

  async getPaginatedConversationsByHelpProviderIdAndQueryOptions(filterOptions, paginationOptions, sortOptions) {

    const path = `${this.basePath}/conversations`;

    return this.apiClient.get(path, {}, { filterOptions, paginationOptions, sortOptions });

  }

  async getOrCreatePublicEmailConversationAndMessageDrafts(divisionIdentityId) {

    const path = `${this.basePath}/conversations/public/:divisionIdentityId`;

    return this.apiClient.post(path, { divisionIdentityId }, {}, {});

  }

  async getOrCreateAssistantConversation() {

    const path = `${this.basePath}/conversations/assistant`;

    return this.apiClient.get(path, {}, {});

  }

  async configureConversationInitialReceiverAndStatus(conversationId, ownerId) {

    const path = `${this.basePath}/conversations/drafts/:conversationId/owner`;

    return this.apiClient.post(path, { conversationId }, {}, { helpProviderId: ownerId });

  }

  async createPrivateEmailConversationAndOutboundMessage(helpSeekerId) {

    const path = `${this.basePath}/conversations/drafts`;

    return this.apiClient.post(path, {}, {}, { receiverId: helpSeekerId });

  }

  async getLiveChatConversationDetail(observerId) {

    const path = `${this.basePath}/conversations/:observerId/live-chat`;

    return this.apiClient.get(path, { observerId }, {});

  }

  async createPrivateEmailConversationWithEmailAddress(payload) {

    const path = `${this.basePath}/conversations/private/email-address`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendFacebookMessage(payload) {

    const path = `${this.basePath}/conversations/facebook`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendInstagramMessage(payload) {

    const path = `${this.basePath}/conversations/instagram`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendSMSMessage(payload) {

    const path = `${this.basePath}/conversations/sms`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendApplicationChatMessage(payload) {

    const path = `${this.basePath}/conversations/chat`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendLinkedInMessage(payload) {

    const path = `${this.basePath}/conversations/linked-in`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async sendTwitterMessage(payload) {

    const path = `${this.basePath}/conversations/twitter`;

    return this.apiClient.post(path, {}, {}, payload);

  }

  async getPopulatedConversationById(conversationId) {

    const path = `${this.basePath}/conversations/:conversationId`;

    return this.apiClient.get(path, { conversationId }, {});

  }

  async getConversationParticipants(conversationId) {

    const path = `${this.basePath}/conversations/:conversationId/participants`;

    return this.apiClient.get(path, { conversationId }, {});

  }

  async updateConversationParticipants(conversationId, participantIds) {

    const path = `${this.basePath}/conversations/:conversationId/participants`;

    return this.apiClient.put(path, { conversationId }, {}, participantIds);

  }

  async getOrCreateConversationMessageDraftForHelpProvider(conversationId) {

    const path = `${this.basePath}/conversations/:conversationId/messages`;

    return this.apiClient.post(path, { conversationId }, {}, {});

  }

  async getOrCreateConversationMessageDraftForIdentity(conversationId, divisionIdentityId) {

    const path = `${this.basePath}/conversations/:conversationId/senders/:divisionIdentityId/messages`;

    return this.apiClient.post(path, { conversationId, divisionIdentityId }, {}, {});

  }

  async setDraftConversationSubjectById(conversationId, conversationMessageId, data) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/subject`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, data);

  }

  async setMessageSender(conversationId, conversationMessageId, senderId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/sender`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, { senderId });

  }

  async updateDraftConversationMessageReceiversById(conversationId, conversationMessageId, data) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/receivers`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, data);

  }

  async updateConversationMessageContentById(conversationId, conversationMessageId, data) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/content`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, data);

  }

  async createOutboundConversationMessage(conversationId, conversationMessageId, content) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/publish`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, content);

  }

  async getPaginatedConversationMessagesByConversationIdAndQueryOptions(conversationId, paginationOptions) {

    const path = `${this.basePath}/conversations/:conversationId/messages`;

    return this.apiClient.get(path, { conversationId }, { paginationOptions });

  }

  async getEmailConversationMessageById(conversationId, conversationMessageId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/email`;

    return this.apiClient.get(path, { conversationId, conversationMessageId }, {}, true);

  }

  async uploadConversationMessageInlineAttachment(conversationId, conversationMessageId, file) {

    let path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId`;

    if (isImage(file)) {
      path = `${path}/images`;
    } else if (isVideo(file)) {
      path = `${path}/videos`;
    } else if (isAudio(file)) {
      path = `${path}/audios`;
    } else if (isDocument(file)) {
      path = `${path}/documents`;
    } else if (xlsxTypes.includes(file.mimetype)) {
      path = `${path}/documents`;
    } else  {
      path = `${path}/documents`;
    }

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.post(path, { conversationId, conversationMessageId }, {}, data, true, false);

  }

  async uploadConversationMessageAttachment(conversationId, conversationMessageId, file) {

    let path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId`;

    if (isImage(file)) {
      path = `${path}/attachments/images`;
    } else if (isVideo(file)) {
      path = `${path}/attachments/videos`;
    } else if (isAudio(file)) {
      path = `${path}/attachments/audios`;
    } else if (isDocument(file) || xlsxTypes.includes(file.mimetype)) {
      path = `${path}/attachments/documents`;
    }

    const data = new FormData();
    data.append('file', file);

    return this.apiClient.post(path, { conversationId, conversationMessageId }, {}, data, true, false);

  }

  async deleteAttachment(conversationId, conversationMessageId, attachmentId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/attachments/:attachmentId`;

    return this.apiClient.delete(path, { conversationId, conversationMessageId, attachmentId });

  }

  async postActivity(conversationId, activity) {

    const path = `${this.basePath}/conversations/:conversationId/activities`;

    return this.apiClient.post(path, { conversationId }, {}, activity);

  }

  async setConversationMessageAsDelivered(conversationId, conversationMessageId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/delivered`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, {});

  }

  async setConversationMessageAsViewed(conversationId, conversationMessageId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId/viewed`;

    return this.apiClient.put(path, { conversationId, conversationMessageId }, {}, {});

  }

  async markConversationWithUserSpaceLabels(conversationId, userSpaceLabelIds) {

    const path = `${this.basePath}/conversations/:conversationId/user-space-labels`;

    return this.apiClient.put(path, { conversationId }, {}, userSpaceLabelIds);

  }

  async deleteOutboundConversationMessageDraft(conversationId, conversationMessageId) {

    const path = `${this.basePath}/conversations/:conversationId/messages/:conversationMessageId`;

    return this.apiClient.delete(path, { conversationId, conversationMessageId }, {}, {});

  }

  async deleteConversationById(conversationId) {

    const path = `${this.basePath}/conversations/:conversationId`;

    return this.apiClient.delete(path, { conversationId }, {}, {});

  }

  async setConversationAutoReply(conversationId, data) {

    const path = `${this.basePath}/conversations/:conversationId/auto-reply`;

    return this.apiClient.post(path, { conversationId }, {}, data);

  }

  async markConversationAsSpam(conversationId, data) {

    const path = `${this.basePath}/conversations/:conversationId/spam`;

    return this.apiClient.put(path, { conversationId }, {}, data);

  }

  async markConversationAsNotSpam(conversationId) {

    const path = `${this.basePath}/conversations/:conversationId/not-spam`;

    return this.apiClient.put(path, { conversationId }, {}, {});

  }

  async getConversationFilters() {

    const path = `${this.basePath}/filters`;

    return this.apiClient.get(path, {}, {});

  }

  async createUserLevelConversationFilter(filter) {

    const path = `${this.basePath}/filters`;

    return this.apiClient.post(path, {}, {}, filter);

  }

  async updateConversationFilterByConversationFilterId(conversationFilterId, filter) {

    const path = `${this.basePath}/filters/:conversationFilterId`;

    return this.apiClient.put(path, { conversationFilterId }, {}, filter);

  }

  async deleteConversationFilterByConversationFilterId(conversationFilterId) {

    const path = `${this.basePath}/filters/:conversationFilterId`;

    return this.apiClient.delete(path, { conversationFilterId }, {});

  }

}

export default StartupHelpdesk;
